// admin/src/scenes/leads/index.jsx
// ─────────────────────────────────────────────────────────────────
// Leads page — three tabs:
//   1. Giveaway Leads    (free chapter claimants)
//   2. Book Buyers       (completed purchases)
//   3. Testimonial Leads (testimonial submitters)
//
// Each lead has:
//   - "Email" button → compose modal (single email)
//   - "Call" button  → tel: link if phone available
//
// Each tab has:
//   - "Email All" button → bulk compose modal
//   - Search/filter bar
// ─────────────────────────────────────────────────────────────────
import {
  Box,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  InputAdornment,
} from "@mui/material";
import {
  Email,
  Phone,
  Send,
  Close,
  Group,
  Search,
  ShoppingCart,
  CardGiftcard,
  Forum,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useEffect, useState, useMemo } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// ── Email Compose Modal ──────────────────────────────────────────
const EmailModal = ({ open, onClose, recipient, groupLeads, colors }) => {
  const isGroup     = !!groupLeads;
  const [subject,   setSubject]   = useState("");
  const [message,   setMessage]   = useState("");
  const [sending,   setSending]   = useState(false);
  const [result,    setResult]    = useState(null); // {success, sent, failed}

  // Pre-fill subject based on context
  useEffect(() => {
    if (!open) { setResult(null); setSubject(""); setMessage(""); return; }
    if (recipient?.source === "giveaway") {
      setSubject(`Ready for the full book, ${recipient.name.split(" ")[0]}?`);
      setMessage(
        `Hi ${recipient.name.split(" ")[0]},\n\nI hope you enjoyed the free chapter of "${recipient.book}"!\n\nI wanted to personally reach out because I believe the full book could make a real difference in your life. It goes much deeper into the practical steps that have helped thousands of readers like you.\n\nYou can get your copy here: https://akothadero.com/books\n\nI'd love to hear your thoughts on the free chapter — feel free to reply to this email anytime.\n\nWith gratitude,\nAkoth Adero`
      );
    } else if (recipient?.source === "buyer") {
      setSubject(`How are you finding "${recipient.book}"?`);
      setMessage(
        `Hi ${recipient.name.split(" ")[0]},\n\nThank you so much for purchasing "${recipient.book}"! I truly hope it's been valuable to you.\n\nI'd love to hear how your reading journey is going. If you have a moment, a short review on the website would mean the world and help others find the book:\nhttps://akothadero.com/books\n\nAlso, I have new books and programs coming up that I think you'd find exciting. Stay tuned!\n\nWith gratitude,\nAkoth Adero`
      );
    } else if (recipient?.source === "testimonial") {
      setSubject(`Thank you for sharing your story`);
      setMessage(
        `Hi ${recipient.name.split(" ")[0]},\n\nThank you so much for taking the time to share your experience. Stories like yours are what motivate me to keep writing and mentoring.\n\nI'd love to stay connected and share some resources that might be helpful for your journey:\nhttps://akothadero.com/programs\n\nWith gratitude,\nAkoth Adero`
      );
    } else if (isGroup) {
      // Group email — generic template
      setSubject("A personal message from Akoth Adero");
      setMessage(
        `Hi there,\n\nI wanted to reach out personally to thank you for your interest in my work.\n\nI have some exciting news to share...\n\n[Write your message here]\n\nWith gratitude,\nAkoth Adero\nhttps://akothadero.com`
      );
    }
  }, [open, recipient, isGroup]);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    setResult(null);

    try {
      const token   = localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };

      if (isGroup) {
        const res = await axios.post(
          `${BASE_URL}/api/admin/leads/email-group`,
          { leads: groupLeads, subject, message },
          { headers }
        );
        setResult({
          success: res.data.success,
          sent:    res.data.sent,
          failed:  res.data.failed,
        });
      } else {
        const res = await axios.post(
          `${BASE_URL}/api/admin/leads/email`,
          { to: recipient.email, name: recipient.name, subject, message },
          { headers }
        );
        setResult({ success: res.data.success, sent: 1, failed: 0 });
      }
    } catch {
      setResult({ success: false, sent: 0, failed: isGroup ? groupLeads.length : 1 });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#1f2937", color: "#f9fafb", borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography fontWeight="bold" fontSize="16px">
            {isGroup
              ? `📧 Email All (${groupLeads.length} leads)`
              : `📧 Email ${recipient?.name}`}
          </Typography>
          {!isGroup && (
            <Typography fontSize="12px" color="#9ca3af">
              {recipient?.email}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#9ca3af" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box display="flex" flexDirection="column" gap="12px">
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ sx: { color: "#9ca3af" } }}
            InputProps={{ sx: { color: "#f9fafb", backgroundColor: "#374151" } }}
          />
          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            rows={10}
            size="small"
            InputLabelProps={{ sx: { color: "#9ca3af" } }}
            InputProps={{ sx: { color: "#f9fafb", backgroundColor: "#374151", fontFamily: "monospace", fontSize: "13px" } }}
          />

          {result && (
            <Alert
              severity={result.success ? "success" : "error"}
              sx={{ fontSize: "13px" }}
            >
              {result.success
                ? isGroup
                  ? `✅ Sent to ${result.sent} leads${result.failed > 0 ? `, ${result.failed} failed` : ""}`
                  : "✅ Email sent successfully!"
                : "❌ Failed to send. Check your Resend config."}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: "#9ca3af" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !subject.trim() || !message.trim() || !!result?.success}
          startIcon={sending ? <CircularProgress size={16} /> : <Send />}
          sx={{ backgroundColor: "#8B5E3C", "&:hover": { backgroundColor: "#6b4a2e" } }}
        >
          {sending ? "Sending…" : isGroup ? `Send to All (${groupLeads?.length})` : "Send Email"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Source chip styles ────────────────────────────────────────────
const SOURCE_CHIP = {
  giveaway:    { label: "Giveaway",    bg: "#1e40af", color: "#bfdbfe", icon: <CardGiftcard sx={{ fontSize: 12 }} /> },
  buyer:       { label: "Buyer",       bg: "#166534", color: "#bbf7d0", icon: <ShoppingCart sx={{ fontSize: 12 }} /> },
  testimonial: { label: "Testimonial", bg: "#7c3aed", color: "#ddd6fe", icon: <Forum sx={{ fontSize: 12 }} /> },
};

// ── Leads table (shared across all 3 tabs) ────────────────────────
const LeadsTable = ({ leads, loading, colors, onEmail }) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return leads;
    return leads.filter(
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.book?.toLowerCase().includes(q)
    );
  }, [leads, search]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Search bar */}
      <TextField
        placeholder="Search by name, email or book…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 2, width: "320px" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: "#9ca3af", fontSize: 18 }} />
            </InputAdornment>
          ),
          sx: { color: colors.grey[100], backgroundColor: colors.primary[400] },
        }}
      />

      <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Name", "Email", "Phone", "Book / Context", "Date", "Actions"].map((h) => (
                <TableCell key={h} sx={{ color: colors.greenAccent[400], fontWeight: "bold", fontSize: "12px" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: colors.grey[300], textAlign: "center", py: 4 }}>
                  {search ? "No leads match your search." : "No leads yet."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((lead) => (
                <TableRow
                  key={lead._id}
                  hover
                  sx={{ "&:hover": { backgroundColor: `${colors.primary[300]}40` } }}
                >
                  <TableCell sx={{ color: colors.grey[100] }}>
                    <Typography fontWeight="bold" fontSize="13px">{lead.name}</Typography>
                    {lead.role && (
                      <Typography fontSize="11px" color={colors.grey[400]}>{lead.role}</Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ color: colors.grey[300], fontSize: "12px" }}>
                    {lead.email}
                  </TableCell>

                  <TableCell sx={{ color: colors.grey[400], fontSize: "12px" }}>
                    {lead.phone ? (
                      <a
                        href={`tel:${lead.phone}`}
                        style={{ color: "#34d399", textDecoration: "none" }}
                      >
                        {lead.phone}
                      </a>
                    ) : (
                      <span style={{ color: "#4b5563" }}>—</span>
                    )}
                  </TableCell>

                  <TableCell sx={{ color: colors.grey[200], fontSize: "12px", maxWidth: "180px" }}>
                    <Typography noWrap fontSize="12px">{lead.book || lead.location || "—"}</Typography>
                    {lead.amount && (
                      <Typography fontSize="11px" color={colors.greenAccent[400]}>
                        KES {Number(lead.amount).toLocaleString()} · {lead.gateway}
                      </Typography>
                    )}
                    {lead.status && (
                      <Chip
                        label={lead.status}
                        size="small"
                        sx={{
                          fontSize: "10px", height: "18px", mt: "2px",
                          backgroundColor: lead.status === "approved" ? "#166534" : lead.status === "pending" ? "#92400e" : "#991b1b",
                          color: "#fff",
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell sx={{ color: colors.grey[400], fontSize: "11px" }}>
                    {new Date(lead.date).toLocaleDateString("en-KE", {
                      day: "numeric", month: "short", year: "numeric",
                    })}
                  </TableCell>

                  <TableCell>
                    <Box display="flex" gap="4px">
                      <Tooltip title="Send Email">
                        <IconButton
                          size="small"
                          onClick={() => onEmail(lead)}
                          sx={{
                            color: colors.greenAccent[400],
                            backgroundColor: `${colors.greenAccent[400]}15`,
                            "&:hover": { backgroundColor: `${colors.greenAccent[400]}30` },
                          }}
                        >
                          <Email sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>

                      {lead.phone && (
                        <Tooltip title={`Call ${lead.phone}`}>
                          <IconButton
                            size="small"
                            component="a"
                            href={`tel:${lead.phone}`}
                            sx={{
                              color: "#60a5fa",
                              backgroundColor: "#60a5fa15",
                              "&:hover": { backgroundColor: "#60a5fa30" },
                            }}
                          >
                            <Phone sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography fontSize="11px" color={colors.grey[500]} mt={1}>
        Showing {filtered.length} of {leads.length} leads
      </Typography>
    </Box>
  );
};

// ── Tab panel with "Email All" header ────────────────────────────
const TabPanel = ({ leads, loading, colors, source, onEmail, onEmailGroup }) => {
  const chip = SOURCE_CHIP[source];

  return (
    <Box>
      {/* Tab header with stats + Email All button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb="16px"
        p="16px"
        sx={{ backgroundColor: colors.primary[400], borderRadius: "8px" }}
      >
        <Box display="flex" alignItems="center" gap="12px">
          <Chip
            label={chip.label}
            icon={chip.icon}
            size="small"
            sx={{ backgroundColor: chip.bg, color: chip.color, fontWeight: "bold" }}
          />
          <Typography color={colors.grey[100]} fontWeight="bold">
            {loading ? "…" : `${leads.length} leads`}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Group />}
          onClick={() => onEmailGroup(leads)}
          disabled={loading || leads.length === 0}
          size="small"
          sx={{
            backgroundColor: "#8B5E3C",
            "&:hover": { backgroundColor: "#6b4a2e" },
            fontSize: "12px",
          }}
        >
          Email All {leads.length > 0 ? `(${leads.length})` : ""}
        </Button>
      </Box>

      <LeadsTable
        leads={leads}
        loading={loading}
        colors={colors}
        onEmail={onEmail}
      />
    </Box>
  );
};

// ── Main Leads Page ───────────────────────────────────────────────
const LeadsPage = () => {
  const theme  = useTheme();
  const colors = tokens(theme.palette.mode);
  const [tab, setTab] = useState(0);

  // Lead data per source
  const [giveaway,     setGiveaway]     = useState([]);
  const [buyers,       setBuyers]       = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loadingG,     setLoadingG]     = useState(true);
  const [loadingB,     setLoadingB]     = useState(true);
  const [loadingT,     setLoadingT]     = useState(true);

  // Email modal state
  const [modalOpen,    setModalOpen]    = useState(false);
  const [modalRecip,   setModalRecip]   = useState(null);  // single lead
  const [modalGroup,   setModalGroup]   = useState(null);  // array of leads

  const adminToken = localStorage.getItem("adminToken");
  const headers    = { Authorization: `Bearer ${adminToken}` };

  // Fetch all three sources on mount
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [gRes, bRes, tRes] = await Promise.allSettled([
          axios.get(`${BASE_URL}/api/admin/leads/giveaway`,     { headers }),
          axios.get(`${BASE_URL}/api/admin/leads/buyers`,       { headers }),
          axios.get(`${BASE_URL}/api/admin/leads/testimonials`, { headers }),
        ]);

        if (gRes.status === "fulfilled") setGiveaway(gRes.value.data.leads || []);
        if (bRes.status === "fulfilled") setBuyers(bRes.value.data.leads || []);
        if (tRes.status === "fulfilled") setTestimonials(tRes.value.data.leads || []);
      } catch (e) {
        console.error("Leads fetch error:", e);
      } finally {
        setLoadingG(false);
        setLoadingB(false);
        setLoadingT(false);
      }
    };
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openSingleEmail = (lead) => {
    setModalRecip(lead);
    setModalGroup(null);
    setModalOpen(true);
  };

  const openGroupEmail = (leads) => {
    setModalGroup(leads);
    setModalRecip(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRecip(null);
    setModalGroup(null);
  };

  const totalLeads =
    (loadingG ? 0 : giveaway.length) +
    (loadingB ? 0 : buyers.length) +
    (loadingT ? 0 : testimonials.length);

  return (
    <Box m="20px">
      <Header
        title="LEADS"
        subtitle={`${totalLeads} total leads across all sources`}
      />

      {/* Summary chips */}
      <Box display="flex" gap="12px" mb="20px" flexWrap="wrap">
        {[
          { label: `${giveaway.length} Giveaway`, ...SOURCE_CHIP.giveaway },
          { label: `${buyers.length} Buyers`,     ...SOURCE_CHIP.buyer },
          { label: `${testimonials.length} Testimonials`, ...SOURCE_CHIP.testimonial },
        ].map((c) => (
          <Chip
            key={c.label}
            label={c.label}
            icon={c.icon}
            sx={{ backgroundColor: c.bg, color: c.color, fontWeight: "bold" }}
          />
        ))}
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: "20px",
          "& .MuiTab-root": { color: colors.grey[300], fontSize: "13px" },
          "& .Mui-selected": { color: `${colors.greenAccent[400]} !important` },
          "& .MuiTabs-indicator": { backgroundColor: colors.greenAccent[400] },
        }}
      >
        <Tab label={`🎁 Giveaway Leads (${giveaway.length})`} />
        <Tab label={`🛒 Book Buyers (${buyers.length})`} />
        <Tab label={`💬 Testimonial Leads (${testimonials.length})`} />
      </Tabs>

      {tab === 0 && (
        <TabPanel
          leads={giveaway}
          loading={loadingG}
          colors={colors}
          source="giveaway"
          onEmail={openSingleEmail}
          onEmailGroup={openGroupEmail}
        />
      )}
      {tab === 1 && (
        <TabPanel
          leads={buyers}
          loading={loadingB}
          colors={colors}
          source="buyer"
          onEmail={openSingleEmail}
          onEmailGroup={openGroupEmail}
        />
      )}
      {tab === 2 && (
        <TabPanel
          leads={testimonials}
          loading={loadingT}
          colors={colors}
          source="testimonial"
          onEmail={openSingleEmail}
          onEmailGroup={openGroupEmail}
        />
      )}

      {/* Email compose modal */}
      <EmailModal
        open={modalOpen}
        onClose={closeModal}
        recipient={modalRecip}
        groupLeads={modalGroup}
        colors={colors}
      />
    </Box>
  );
};

export default LeadsPage;
